import React, { createContext, useContext, useEffect, useState, useMemo } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const STORAGE_KEY = 'apex-lens-theme';

const AppContext = createContext(null);

function loadPersisted() {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

export function AppProvider({ children }) {
  const persisted = loadPersisted();
  const [seed, setSeed] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [role, setRole] = useState(persisted.role || 'CISO');
  const [demoOpen, setDemoOpen] = useState(false);
  const [demoStep, setDemoStep] = useState(0);
  const [partnerModalOpen, setPartnerModalOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [dataSecurityOpen, setDataSecurityOpen] = useState(false);

  useEffect(() => {
    let cancel = false;
    axios.get(`${API}/seed`).then((r) => {
      if (cancel) return;
      setSeed(r.data);
      setLoading(false);
    }).catch((e) => {
      if (cancel) return;
      setError(e?.message ?? 'failed to load seed');
      setLoading(false);
    });
    return () => { cancel = true; };
  }, []);

  // Persist role
  useEffect(() => {
    try {
      const prev = loadPersisted();
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...prev, role }));
    } catch { /* ignore */ }
  }, [role]);

  const value = useMemo(() => ({
    API, seed, loading, error,
    role, setRole,
    demoOpen, setDemoOpen,
    demoStep, setDemoStep,
    partnerModalOpen, setPartnerModalOpen,
    sidebarOpen, setSidebarOpen,
    dataSecurityOpen, setDataSecurityOpen,
  }), [seed, loading, error, role, demoOpen, demoStep, partnerModalOpen, sidebarOpen, dataSecurityOpen]);

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
}
